import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TIME_SLOTS, STUDY_PATTERNS } from '../../../utils/studyPlanMockData';
import { StudyPattern, SleepPattern } from '../../../types/study-plan';

interface StudyPatternsStepProps {
  onNext: (data: { studyPattern: StudyPattern; sleepPattern: SleepPattern }) => void;
  onBack: () => void;
  defaultValue?: { studyPattern: StudyPattern; sleepPattern: SleepPattern };
}

const StudyPatternsStep: React.FC<StudyPatternsStepProps> = ({ onNext, onBack, defaultValue }) => {
  const [studyPattern, setStudyPattern] = useState<StudyPattern>(
    defaultValue?.studyPattern || { preferredTime: 'morning', selectedSlots: [] }
  );
  const [sleepPattern, setSleepPattern] = useState<SleepPattern>(
    defaultValue?.sleepPattern || { bedTime: '22:00', wakeUpTime: '06:00' }
  );

  const handlePreferredTimeChange = (time: 'morning' | 'afternoon' | 'evening' | 'night') => {
    setStudyPattern(prev => ({ ...prev, preferredTime: time }));
  };

  const handleSlotToggle = (slotId: string) => {
    setStudyPattern(prev => ({
      ...prev,
      selectedSlots: prev.selectedSlots.includes(slotId)
        ? prev.selectedSlots.filter(id => id !== slotId)
        : [...prev.selectedSlots, slotId]
    }));
  };

  const handleSleepTimeChange = (field: 'bedTime' | 'wakeUpTime', value: string) => {
    setSleepPattern(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = () => {
    if (studyPattern.selectedSlots.length === 0) {
      alert('Please select at least one study time slot');
      return;
    }
    onNext({ studyPattern, sleepPattern });
  };

  const getTimeIcon = (time: string) => {
    switch (time) {
      case 'morning': return 'fa-sun';
      case 'afternoon': return 'fa-sun';
      case 'evening': return 'fa-moon';
      case 'night': return 'fa-moon';
      default: return 'fa-clock';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      transition={{ duration: 0.3 }}
      className="step-content"
    >
      <div className="container-fluid py-2 py-md-4">
        <div className="row justify-content-center">
          <div className="col-12 col-lg-10">
            <div className="text-center mb-4 mb-md-5">
              <h2 className="display-6 fw-bold text-dark mb-3">
                Define Your Study & Sleep Patterns
              </h2>
              <p className="lead text-muted">
                Help us create a schedule that fits your natural rhythm
              </p>
            </div>

            {/* Study Pattern */}
            <div className="card mb-4 mb-md-5 border shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0 text-dark">
                  <i className="fas fa-book-open me-2 text-info"></i>
                  Study Pattern Preference
                </h5>
              </div>
              <div className="card-body bg-light">
                <div className="row g-2 g-md-3 mb-4">
                  {STUDY_PATTERNS.map((pattern) => (
                    <div key={pattern.id} className="col-6 col-md-6 col-lg-3">
                      <motion.div
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className={`card border-2 cursor-pointer h-100 position-relative ${
                          studyPattern.preferredTime === pattern.value
                            ? 'border-info bg-info shadow-sm'
                            : 'border-light hover-shadow'
                        }`}
                        onClick={() => handlePreferredTimeChange(pattern.value as any)}
                      >
                        {studyPattern.preferredTime === pattern.value && (
                          <div className="position-absolute top-0 end-0 m-2">
                            <div className="bg-white text-info rounded-circle d-flex align-items-center justify-content-center border border-info" 
                                 style={{ width: '24px', height: '24px' }}>
                              <i className="fas fa-check" style={{ fontSize: '12px' }}></i>
                            </div>
                          </div>
                        )}
                        <div className="card-body text-center p-3">
                          <div className={`mb-2 ${
                            studyPattern.preferredTime === pattern.value ? 'text-white' : 'text-info'
                          }`}>
                            <i className={`fas ${getTimeIcon(pattern.value)} fa-2x`}></i>
                          </div>
                          <h6 className={`fw-bold mb-1 ${
                            studyPattern.preferredTime === pattern.value 
                              ? 'text-white' 
                              : 'text-dark'
                          }`}>
                            {pattern.value.charAt(0).toUpperCase() + pattern.value.slice(1)}
                          </h6>
                          <small className={`${
                            studyPattern.preferredTime === pattern.value ? 'text-white opacity-75' : 'text-muted'
                          }`}>{pattern.label}</small>
                        </div>
                      </motion.div>
                    </div>
                  ))}
                </div>

                <div className="border-top pt-4 mt-4 bg-white rounded p-3">
                  <h6 className="text-dark fw-bold mb-3">
                    <i className="fas fa-clock me-2 text-info"></i>
                    Select Your Available Study Time Slots
                  </h6>
                  <div className="row g-2">
                    {TIME_SLOTS.map((slot) => (
                      <div key={slot.id} className="col-6 col-sm-4 col-md-4 col-lg-3">
                        <div
                          className={`card border-2 cursor-pointer position-relative ${
                            studyPattern.selectedSlots.includes(slot.id)
                              ? 'border-info bg-info shadow-sm'
                              : 'border-light hover-shadow'
                          }`}
                          onClick={() => handleSlotToggle(slot.id)}
                        >
                          {studyPattern.selectedSlots.includes(slot.id) && (
                            <div className="position-absolute top-0 end-0 m-1">
                              <div className="bg-white text-info rounded-circle d-flex align-items-center justify-content-center border border-info" 
                                   style={{ width: '20px', height: '20px' }}>
                                <i className="fas fa-check" style={{ fontSize: '10px' }}></i>
                              </div>
                            </div>
                          )}
                          <div className="card-body p-3 text-center">
                            <h6 className={`mb-1 ${
                              studyPattern.selectedSlots.includes(slot.id) 
                                ? 'text-white' 
                                : 'text-dark'
                            }`}>
                              {slot.label}
                            </h6>
                            <small className={`${
                              studyPattern.selectedSlots.includes(slot.id) ? 'text-white opacity-75' : 'text-muted'
                            }`}>
                              {slot.startTime} - {slot.endTime}
                            </small>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted d-block mt-2">
                    <i className="fas fa-info-circle me-1"></i>
                    Select multiple slots for flexible scheduling
                  </small>
                </div>
              </div>
            </div>

            {/* Sleep Pattern */}
            <div className="card mb-4 mb-md-5 border shadow-sm">
              <div className="card-header bg-white border-bottom">
                <h5 className="mb-0 text-dark">
                  <i className="fas fa-bed me-2 text-info"></i>
                  Sleep Schedule
                </h5>
              </div>
              <div className="card-body bg-light">
                <div className="row g-4">
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-moon me-2 text-info"></i>
                      Bedtime
                    </label>
                    <input
                      type="time"
                      className="form-control form-control-lg border-2 border-info"
                      value={sleepPattern.bedTime}
                      onChange={(e) => handleSleepTimeChange('bedTime', e.target.value)}
                    />
                    <small className="text-muted">When do you usually go to bed?</small>
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-semibold text-dark">
                      <i className="fas fa-sun me-2 text-info"></i>
                      Wake-up Time
                    </label>
                    <input
                      type="time"
                      className="form-control form-control-lg border-2 border-info"
                      value={sleepPattern.wakeUpTime}
                      onChange={(e) => handleSleepTimeChange('wakeUpTime', e.target.value)}
                    />
                    <small className="text-muted">When do you usually wake up?</small>
                  </div>
                </div>
                
                <div className="mt-3 p-3 bg-light rounded">
                  <small className="text-muted">
                    <i className="fas fa-lightbulb me-2 text-warning"></i>
                    <strong>Tip:</strong> Maintaining a consistent sleep schedule helps improve focus and memory retention during study sessions.
                  </small>
                </div>
              </div>
            </div>

            <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
              <button
                type="button"
                onClick={onBack}
                className="btn btn-outline-secondary btn-lg px-4 order-2 order-md-1"
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back
              </button>
              
              <button
                type="button"
                onClick={handleSubmit}
                className="btn btn-info btn-lg px-5 order-1 order-md-2"
                disabled={studyPattern.selectedSlots.length === 0}
              >
                Continue
                <i className="fas fa-arrow-right ms-2"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StudyPatternsStep;